import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/lib/i18n";
import { SearchProvider } from "@/lib/search";
import { ContentProvider } from "@/lib/content";
import { ConciergeProvider } from "@/components/concierge/ConciergeContext";
import { FjordvertenWidget } from "@/components/concierge/FjordvertenWidget";
import { SiteLayout, ScrollToTop } from "@/components/layout/SiteLayout";
import Home from "@/pages/Home";
import Rooms from "@/pages/Rooms";
import RoomDetail from "@/pages/RoomDetail";
import Booking from "@/pages/Booking";
import MyBooking from "@/pages/MyBooking";
import Dining from "@/pages/Dining";
import Opplev from "@/pages/Opplev";
import Events from "@/pages/Events";
import Practical from "@/pages/Practical";
import About from "@/pages/About";
import NotFound from "@/pages/NotFound";

const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

const AdminFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground" data-testid="admin-loading">…</div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LangProvider>
        <SearchProvider>
          <ContentProvider>
            <ConciergeProvider>
              <BrowserRouter>
                <ScrollToTop />
                <Routes>
                  <Route element={<SiteLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/rom" element={<Rooms />} />
                    <Route path="/rom/:slug" element={<RoomDetail />} />
                    <Route path="/bestill" element={<Booking />} />
                    <Route path="/mi-bestilling" element={<MyBooking />} />
                    <Route path="/mat" element={<Dining />} />
                    <Route path="/opplev" element={<Opplev />} />
                    <Route path="/arrangement" element={<Events />} />
                    <Route path="/praktisk" element={<Practical />} />
                    <Route path="/om-oss" element={<About />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                  <Route
                    path="/admin/*"
                    element={
                      <Suspense fallback={<AdminFallback />}>
                        <AdminApp />
                      </Suspense>
                    }
                  />
                </Routes>
                <FjordvertenWidget />
                <Toaster position="top-center" richColors={false} closeButton />
              </BrowserRouter>
            </ConciergeProvider>
          </ContentProvider>
        </SearchProvider>
      </LangProvider>
    </QueryClientProvider>
  );
}

export default App;
